/* DesiTech GRQ components. Isolated from Symmetry 8's native variant/gallery components. */
(() => {
  if (customElements.get('desitech-media-gallery')) return;
  // Reflect variant availability without permanently disabling checkout after a sold-out selection.
  document.addEventListener('on:variant:change', event => {
    const product = event.target.closest('.js-product');
    product?.querySelectorAll('.shiprocket-headless[data-type="product"]').forEach(mount => {
      mount.inert = !event.detail.variant?.available;
      mount.setAttribute('aria-disabled', String(mount.inert));
    });
  });
  let swiperPromise;
  function loadSwiper(src) {
    if (window.Swiper) return Promise.resolve(window.Swiper);
    if (!swiperPromise) {
      swiperPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve(window.Swiper);
        script.onerror = () => { swiperPromise = undefined; script.remove(); reject(new Error('GRQ gallery library could not load')); };
        document.head.appendChild(script);
      });
    }
    return swiperPromise;
  }
  class DesitechMediaGallery extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      this.product = this.closest('.js-product');
      this.product?.addEventListener('on:variant:change', event => {
        const variant = event.detail.variant;
        const title = this.product.querySelector('[data-variant-title]');
        if (title) title.textContent = variant?.title && variant.title !== 'Default Title' ? ` - ${variant.title}` : '';
        if (variant?.featured_media) this.setActiveMedia(variant.featured_media.id);
      }, { signal });
      this.addEventListener('click', event => {
        const anchor = event.target.closest('a.show-gallery');
        if (!anchor) return;
        const template = this.querySelector('.js-media-zoom-template');
        if (!template) return;
        event.preventDefault();
        if (!this.modal) {
          this.modal = template.content.firstElementChild.cloneNode(true);
          document.body.appendChild(this.modal);
        }
        if (typeof this.modal.open === 'function') {
          this.modal.open(anchor);
          const viewer = this.modal.querySelector('gallery-viewer');
          viewer?.init(anchor.href);
          viewer?.focus();
        }
      }, { signal });
      loadSwiper(this.dataset.swiperSrc).then(Swiper => {
        if (signal.aborted || !this.isConnected) return;
        const thumbs = this.querySelector('.thumbnail-slider');
        const main = this.querySelector('.main-slider');
        if (!main) return;
        this.thumbs = thumbs ? new Swiper(thumbs, {
          spaceBetween: 10, slidesPerView: 4, freeMode: true, watchSlidesProgress: true
        }) : undefined;
        const slides = Array.from(main.querySelectorAll('.swiper-slide'));
        const initialIndex = Math.max(0, slides.findIndex(s => s.dataset.mediaId === this.dataset.initialMediaId));
        this.slider = new Swiper(main, {
          spaceBetween: 10, loop: slides.length > 1, initialSlide: initialIndex,
          navigation: { nextEl: this.querySelector('.main-next'), prevEl: this.querySelector('.main-prev') },
          thumbs: this.thumbs ? { swiper: this.thumbs } : undefined,
          on: { slideChange: () => this.pauseInactiveMedia() }
        });
        this.setAttribute('loaded', '');
        if (this.pendingMediaId) this.setActiveMedia(this.pendingMediaId);
      }).catch(() => {
        // Leave the first image visible if a network error prevents enhancement.
        this.classList.add('desitech-gallery-fallback');
      });
    }
    setActiveMedia(id) {
      this.pendingMediaId = id;
      if (!this.slider) return;
      const slides = Array.from(this.querySelectorAll('.main-slider .swiper-slide'));
      const slide = slides.find(s => s.dataset.mediaId === String(id));
      if (!slide) return;
      const index = Number(slide.dataset.swiperSlideIndex ?? slides.indexOf(slide));
      if (this.slider.params.loop) this.slider.slideToLoop(index);
      else this.slider.slideTo(index);
    }
    pauseInactiveMedia() {
      this.querySelectorAll('.main-slider .swiper-slide:not(.swiper-slide-active) video').forEach(video => video.pause());
      this.dispatchEvent(new CustomEvent('on:media-gallery:change', { bubbles: true }));
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = undefined;
      this.slider?.destroy(true, true);
      this.thumbs?.destroy(true, true);
      this.modal?.remove();
      this.slider = this.thumbs = this.modal = undefined;
    }
  }
  customElements.define('desitech-media-gallery', DesitechMediaGallery);

  if (!customElements.get('variant-content')) {
    class DesitechVariantContent extends HTMLElement {
      connectedCallback() {
        this.product = this.closest('.js-product');
        this.handler = event => {
          [...this.childNodes].filter(node => node.nodeName !== 'SCRIPT').forEach(node => node.remove());
          const template = this.querySelector(`[data-variant="${event.detail.variant?.id || ''}"]`);
          if (template) this.insertAdjacentHTML('beforeend', template.innerHTML);
        };
        this.product?.addEventListener('on:variant:change', this.handler);
      }
      disconnectedCallback() { this.product?.removeEventListener('on:variant:change', this.handler); }
    }
    customElements.define('variant-content', DesitechVariantContent);
  }
  if (!customElements.get('product-inventory')) {
    class DesitechInventory extends HTMLElement {
      connectedCallback() {
        this.product = this.closest('.js-product');
        this.inventory = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '[]');
        this.handler = event => {
          const data = this.inventory.find(item => item.id === event.detail.variant?.id);
          const el = this.querySelector('.product-inventory');
          if (!el) return;
          el.hidden = !data;
          if (!data) return;
          const low = data.inventory_quantity <= Number(this.dataset.threshold);
          const showCount = this.dataset.showInventoryCount === 'always' || (this.dataset.showInventoryCount === 'low' && low);
          const text = showCount ? (low ? this.dataset.textXLeftLow : this.dataset.textXLeftOk) : (low ? this.dataset.textLow : this.dataset.textOk);
          this.querySelector('.product-inventory__status').textContent = text.replace('[QTY]', data.inventory_quantity);
          el.classList.toggle('product-inventory--low', low);
          el.classList.toggle('product-inventory--ok', !low);
        };
        this.product?.addEventListener('on:variant:change', this.handler);
      }
      disconnectedCallback() { this.product?.removeEventListener('on:variant:change', this.handler); }
    }
    customElements.define('product-inventory', DesitechInventory);
  }
})();
